// rraCopy — every RRA v2.1 string a requester, household member, verifier or
// front-line caller reads, plus the few staff strings both the API and the SPA
// build. docs/design/rra-v2-1-implementation.md §1.9; the words come from
// docs/design/rra-v2-adversarial-review.md §1 (verbatim) unless marked DRAFT.
//
// LOCK-STEP: apps/api/src/lib/rra/rraCopy.ts and apps/web/src/lib/rraCopy.ts are
// byte-identical (apps/api/src/lib/rra/rraCopy.test.ts). Edit the API file, copy
// it over the web one. Import-free and pure. No string here carries an id.

export const RRA_HELP_MAX = 300;
export const RRA_HELP_COUNTER_FROM = 250;
export const RRA_REQUEST_TEXT_MAX = 300;
export const RRA_CALL_NOTE_MAX = 300;
export const RRA_DECLINE_GROUND_MAX = 200;
export const RRA_LEASING_EMAIL = 'leasing@welcomehps.com';

/** Whose voice the copy is in (B2). readerIsSubject: the reader is the person with the
 *  disability. name: the subject's name ('' when readerIsSubject). */
export interface RraCopySubject {
  readerIsSubject: boolean;
  name: string;
  minor: boolean;
}

// --- B1 -------------------------------------------------------------------
export function formIntro(officePhone: string): string {
  return `This short form puts your request in writing. Nothing about this request is decided against you while it's open, and no deadline closes it. Please don't include a diagnosis or medical records. We don't need them. Prefer to do this by phone? Call us at ${officePhone} and we'll fill it in with you.`;
}
/** B1's second paragraph. Always shown: its condition is met (F1–F4 ship as Miles settled
 *  them on 2026-10-23, and Decision 1 is settled). */
export const FORM_PRIVACY_LINE =
  'What you tell us here is seen only by the small team that handles these requests, and it never appears on your lease.';

// --- B2 -------------------------------------------------------------------
export const WHO_FOR = {
  heading: 'Who is this request for?',
  me: 'Me',
  someone: 'Someone who lives with me, or will',
  nameLabel: 'Their first and last name',
  minorLabel: 'Are they under 18?',
  yes: 'Yes',
  no: 'No',
} as const;

// --- B3 -------------------------------------------------------------------
export const REQUEST_HEADING = "What you're asking us to change";
export function requestHelper(animalCase: boolean): string {
  return animalCase
    ? "Tell us what you'd like us to change. For an assistance animal, that's our animal rule — we've checked it for you. Uncheck it if it doesn't fit."
    : "Tell us what you'd like us to change.";
}
export function allowAnimalLabel(s: RraCopySubject): string {
  return s.readerIsSubject
    ? "Let my animal live with me, even where your animal rules wouldn't otherwise allow it."
    : `Let ${s.name}'s animal live with us, even where your animal rules wouldn't otherwise allow it.`;
}
export const NO_ANIMAL_CHARGES_LINE = "If we approve your animal, there's no extra rent, fee, or deposit for it.";
export const OTHER_RULE_LABEL = 'A change to another rule, or to how you do things';
export const OTHER_RULE_INPUT = 'What would you like us to change?';
export const PHYSICAL_CHANGE_LABEL = 'A physical change to the home or the building';
export const PHYSICAL_CHANGE_INPUT = 'What would you like changed?';
export const REQUEST_ERROR = "Tell us what you'd like us to change.";
/** RRA v2.2 (WEL-180): the per-request label when a case carries several — "Request 1",
 *  "Request 2". The accommodation stage collects no animal details any more, so an animal's
 *  name is never the label; the request is what they asked us to change. Shared by the form,
 *  the request PDF and the console. */
export function requestLabel(n: number): string {
  return `Request ${n}`;
}

// --- B6 -------------------------------------------------------------------
export function animalSectionHeading(s: RraCopySubject): string {
  return s.readerIsSubject ? 'Your animal' : `${s.name}'s animal`; // household form: DRAFT
}

// --- B4 -------------------------------------------------------------------
export const HELP_HEADING = 'How this would help';
export function helpLabel(s: RraCopySubject, item: { animal: boolean; animalName?: string | undefined }): string {
  if (item.animal) {
    const it = item.animalName?.trim() || (s.readerIsSubject ? 'your animal' : 'their animal');
    return s.readerIsSubject
      ? `How does having ${it} with you help you at home? (optional)`
      : `How does having ${it} with ${s.name} help ${s.name} at home? (optional)`;
  }
  return s.readerIsSubject
    ? 'How would this change help you live in your home? (optional)'
    : `How would this change help ${s.name} live in their home? (optional)`;
}
export function helpHelper(s: RraCopySubject): string {
  return s.readerIsSubject
    ? "A sentence or two is plenty. Please leave out your disability, symptoms, and treatment. We don't need them."
    : `A sentence or two is plenty. Please leave out ${s.name}'s disability, symptoms, and treatment. We don't need them.`;
}
/** "{n}/300", shown from 250 characters; null below. */
export function helpCounter(length: number): string | null {
  return length >= RRA_HELP_COUNTER_FROM ? `${length}/${RRA_HELP_MAX}` : null;
}
export const HELP_TOO_LONG = 'Please shorten this to 300 characters or fewer.';

// --- B5 -------------------------------------------------------------------
export interface RraDefinitionText {
  intro: string;
  bullets: [string, string, string];
  tail: string;
}
/** ONE constant on every surface. "You" becomes the subject's name wherever the reader
 *  is not the subject (household form, verifier email, verify page, call sheet). */
export function disabilityDefinition(s: { readerIsSubject: boolean; name: string }): RraDefinitionText {
  const intro = 'Under Washington law, a disability is a sensory, mental, or physical impairment that:';
  if (s.readerIsSubject) {
    return {
      intro,
      bullets: [
        'a health professional could recognize or diagnose, or',
        'you have a record or history of, or',
        'others perceive you to have.',
      ],
      tail: 'It can be temporary or permanent, common or uncommon, and it counts even when treatment or medication controls it. It does not have to limit your ability to work or to carry out other activities.',
    };
  }
  return {
    intro,
    bullets: [
      'a health professional could recognize or diagnose, or',
      `${s.name} has a record or history of, or`,
      `others perceive ${s.name} to have.`,
    ],
    tail: `It can be temporary or permanent, common or uncommon, and it counts even when treatment or medication controls it. It does not have to limit ${s.name}'s ability to work or to carry out other activities.`,
  };
}
/** Plain-text rendering (email text frames): intro, "- " bullets, tail. */
export function definitionPlainText(d: RraDefinitionText): string {
  return `${d.intro}\n${d.bullets.map((b) => `- ${b}`).join('\n')}\n\n${d.tail}`;
}
export const SPOKEN_DEFINITION =
  "Washington defines disability broadly: any physical, mental, or sensory condition a health professional could recognize or diagnose, or that the person has a history of. It counts even when treatment controls it, and it doesn't have to limit work or daily life.";
export function disabilityHeading(s: RraCopySubject): string {
  return s.readerIsSubject ? 'Your disability' : `${s.name}'s disability`; // household: DRAFT
}
export function attestationLine(s: RraCopySubject): string {
  return s.readerIsSubject ? 'I have a disability as described above.' : `${s.name} has a disability as described above.`;
}
export const DEFINITION_I_READ = 'The definition I read:';

// --- B7 -------------------------------------------------------------------
export const AFFIRMATION_HEADING = 'Your affirmation';
export function needAffirmation(s: RraCopySubject, animal: boolean): string {
  if (s.readerIsSubject) {
    return animal
      ? "I need the change I'm asking for because of my disability. My animal does tasks for me or gives me support that eases the effects of my disability."
      : "I need the change I'm asking for because of my disability.";
  }
  return animal
    ? `${s.name} needs the change I'm asking for because of their disability. Their animal does tasks for them or gives them support that eases the effects of their disability.`
    : `${s.name} needs the change I'm asking for because of their disability.`;
}

// --- Service-animal questions (kept exactly; shown only on a service_animal item with need in the gap) ---
export const SERVICE_ANIMAL_REQUIRED_Q = 'Is this animal needed because of a disability?';
export const SERVICE_ANIMAL_TASKS_Q =
  "What support does the animal provide, or what tasks does it perform? Please don't include a diagnosis or medical details.";

// --- B8 -------------------------------------------------------------------
export const CONFIRM_HEADING = 'Confirming your request';
export function confirmIntro(s: RraCopySubject): string {
  return s.readerIsSubject
    ? "If your disability or your need isn't something we'd already know, we may need one simple confirmation. You can name someone now, upload a letter you already have, or decide later — your request stays open either way."
    : `If ${s.name}'s disability or need isn't something we'd already know, we may need one simple confirmation. You can name someone now, upload a letter you already have, or decide later — your request stays open either way.`; // household: DRAFT
}
export const CONFIRM_CHOICE = {
  contacts: 'Name someone we may contact',
  letter: 'Upload a letter I already have',
  later: "I'll decide later. If we need a confirmation, we'll ask you first.",
} as const;
export const LETTER_DIAGNOSIS_OK = "If your letter names a diagnosis, that's okay. We don't need it and won't ask about it.";
export const LETTER_UPLOAD_LABEL = 'Upload your letter (PDF)';
export const LETTER_TO_FINISH = 'Attach your letter above to finish.';
export const VERIFIER_NAME_LABEL = 'Name';
export function verifierRoleLabel(s: RraCopySubject): string {
  return s.readerIsSubject
    ? 'How they know your situation (e.g. therapist, case worker — optional)'
    : `How they know ${s.name}'s situation (e.g. therapist, case worker — optional)`; // household: DRAFT
}
export const VERIFIER_EMAIL_LABEL = 'Email (if you have it)';
export const VERIFIER_EMAIL_HELPER =
  "An email address is the fastest way. Most offices need your signed permission in writing before they'll answer.";
export const VERIFIER_PHONE_LABEL = 'Phone (if you have it)';
export const ADD_PERSON = 'Add another person';
export function adultSubjectPermissionLine(name: string): string {
  return `${name} needs to give this permission themselves. Enter their email and we'll send them a short link, or have them type their own name below.`;
}
export function subjectLinkChoice(name: string): string { return `Send ${name} a short link`; }           // DRAFT
export function subjectHereChoice(name: string): string { return `${name} is here to sign`; }            // DRAFT
export function subjectEmailLabel(name: string): string { return `${name}'s email`; }                     // DRAFT
export function subjectSignLabel(name: string): string { return `${name} types their full name to sign the permission`; } // DRAFT

// --- D1 -------------------------------------------------------------------
export const PERMISSION_BOX_TITLE = "What you're signing";
export const PERMISSION_NAME_PLACEHOLDER = 'the person you name'; // DRAFT — shown in the box until a name is typed
export interface RraPermissionText {
  lead: string;
  bullets: string[];
  guardianLine?: string;
}
/** Counsel's final text (D1), one block per named person. signer 'subject' = the person
 *  with the disability signs (self, or an adult household member signing their own);
 *  'parent_guardian' = a parent or guardian signs for a minor (subject references re-voiced,
 *  the signer's own acts stay first person — see the sheet's spec decisions). */
export function permissionText(p: {
  verifierName: string;
  signer: 'subject' | 'parent_guardian';
  subjectName: string;
  needIsAnimal: boolean;
  officePhone: string;
}): RraPermissionText {
  const v = p.verifierName.trim() || PERMISSION_NAME_PLACEHOLDER;
  const common2 = `Welcome Home Properties will send ${v} a copy of my request and this permission. It won't send what I wrote about how the change would help.`;
  const common3 = 'This permission ends when Welcome Home Properties decides my request, or one year after I sign, whichever comes first.';
  const common4 = `I can withdraw this permission at any time by emailing ${RRA_LEASING_EMAIL} or calling ${p.officePhone}. Welcome Home Properties will tell anyone it has contacted, in writing. Withdrawing doesn't undo an answer already given, and my request stays open either way.`;
  const common6 = `After ${v} tells Welcome Home Properties, federal health privacy law may no longer protect that information, and it could be shared again. Welcome Home Properties keeps it confidential and uses it only to decide my request.`;
  if (p.signer === 'subject') {
    const needObject = p.needIsAnimal ? 'my animal at home' : 'the change requested';
    return {
      lead: `I give Welcome Home Properties permission to contact ${v}, for one purpose only: to ask whether I have a disability, and whether I need ${needObject} because of it.`,
      bullets: [
        `I authorize ${v} to tell Welcome Home Properties only that, in writing or by phone. Not my diagnosis, my records, or anything else about me.`,
        common2,
        common3,
        common4,
        "My medical care, payment, and coverage don't depend on whether I sign this.",
        common6,
      ],
    };
  }
  const n = p.subjectName;
  const needObject = p.needIsAnimal ? `${n}'s animal at home` : 'the change requested';
  return {
    lead: `I give Welcome Home Properties permission to contact ${v}, for one purpose only: to ask whether ${n} has a disability, and whether ${n} needs ${needObject} because of it.`,
    bullets: [
      `I authorize ${v} to tell Welcome Home Properties only that, in writing or by phone. Not ${n}'s diagnosis, ${n}'s records, or anything else about ${n}.`,
      common2,
      common3,
      common4,
      `${n}'s medical care, payment, and coverage don't depend on whether I sign this.`,
      common6,
    ],
    guardianLine: `I'm signing as ${n}'s parent or legal guardian.`,
  };
}

// --- B9 / B10 / B11 --------------------------------------------------------
export const CONFIRM_ONLY_QUESTION = "Is this what you're asking for?";
export const SEND_MY_REQUEST = 'Send my request';
export const SIGN_LABEL = 'Type your full name to sign';
export const SIGN_HELPER =
  "By typing your name, you sign this request and confirm that what you've told us is true to the best of your knowledge.";
export const SIGN_HELPER_CONTACTS = 'This also signs the permission above for each person you named.';
export const SUBMIT_SIGNED = 'Sign and submit my request';
export const STILL_NEEDED = 'Almost there. Still needed:';
export const SUBMITTED_HEADING = 'Thank you';
export const SUBMITTED_SIGNED = 'Your request is signed and with our team.';
export const SUBMITTED_CONFIRMED = 'Your request is with our team.'; // DRAFT (B9: nothing is signed)
export const DOWNLOAD_SIGNED_COPY = 'Download a copy of your signed request';
export const DOWNLOAD_CONFIRMED_COPY = 'Download a copy of your request'; // DRAFT (B9)
export function nextStepLine(p: { path: 'contacts' | 'later' | 'letter' | 'apparent' | 'subject_link'; firstVerifierName?: string; subjectName?: string }): string {
  if (p.path === 'contacts' && p.firstVerifierName) return `If we need a confirmation, we'll contact ${p.firstVerifierName}, the person you named.`;
  if (p.path === 'later') return "If we need a confirmation, we'll ask you first.";
  if (p.path === 'subject_link' && p.subjectName) return `We've sent ${p.subjectName} a short link to give their permission.`; // DRAFT
  return "We'll be in touch about your request.";
}

// --- A6 / B2 permission page + emails --------------------------------------
export const PERMISSION_ASK_EMAIL = {
  subject: 'One question about your request',
  body: "To finish confirming your request, it would help to hear from someone who knows about your need. That could be a counselor, doctor or case worker, or someone else who knows you well. If you're comfortable, you can name them here, or send us a letter you already have. Your request stays open either way.",
  button: 'Name someone, or add a letter',
} as const;
/** B2 adult member's link email — DRAFT (no consensus copy exists). */
export function subjectPermissionEmail(p: { requesterName: string; summary: string }): { subject: string; body: string; button: string } {
  return {
    subject: `${p.requesterName} asked us to send you this`,
    body: `${p.requesterName} has asked Welcome Home Properties, on your behalf, to ${p.summary} as a reasonable accommodation. To confirm it, it would help to hear from someone who knows about your need, and only you can give us permission to contact them. If you're comfortable, you can name them here, or send us a letter you already have. The request stays open either way.`,
    button: PERMISSION_ASK_EMAIL.button,
  };
}
export const PERMISSION_PAGE = {
  heading: CONFIRM_HEADING,
  intro: 'Name someone who can confirm the request, or upload a letter you already have. The request stays open either way.', // DRAFT
  signHelper: 'By typing your name, you sign the permission above for each person you named.', // DRAFT
  submit: 'Sign and send', // DRAFT
  sendLetter: 'Send my letter', // DRAFT
  done: "Thank you. We have what we need for now, and we'll be in touch.", // DRAFT
} as const;

// --- C1 / E1 request summary -------------------------------------------------
export function requestSummary(requests: Array<{ allowAnimal?: boolean | undefined; otherRule?: string | undefined; physicalChange?: string | undefined }>): string {
  const phrases: string[] = [];
  if (requests.some((r) => r.allowAnimal)) phrases.push('permit an assistance animal');
  if (requests.some((r) => (r.otherRule ?? '').trim())) phrases.push('change a rule or policy');
  if (requests.some((r) => (r.physicalChange ?? '').trim())) phrases.push('allow a change to the home');
  if (phrases.length === 0) return 'consider this request';
  if (phrases.length === 1) return phrases[0]!;
  if (phrases.length === 2) return `${phrases[0]} and ${phrases[1]}`;
  return `${phrases.slice(0, -1).join(', ')}, and ${phrases[phrases.length - 1]}`;
}

// --- E2 one question set everywhere ------------------------------------------
export function verifierNeedObject(p: { animalCount: number; otherCount: number }): string {
  if (p.animalCount > 0 && p.otherCount > 0) return 'their animal at home and the other change they asked for';
  if (p.animalCount > 1) return 'their animals at home';
  if (p.animalCount === 1) return 'their animal at home';
  return 'the change they asked for';
}
export function verificationAskText(p: { name: string; askDisability: boolean; askNeed: boolean; needObject: string }): string {
  if (p.askDisability && p.askNeed) {
    return `two things only: whether ${p.name} has a disability as Washington law defines it, and whether they need ${p.needObject} because of it`;
  }
  if (p.askDisability) return `one thing only: whether ${p.name} has a disability as Washington law defines it`;
  return `one thing only: whether ${p.name} needs ${p.needObject} because of their disability`;
}
export function positionQuestion(who: string): string {
  return `Do you know ${who} in a way that lets you answer?`;
}
export const POSITION_NO_THANKS = "Thank you — that's a helpful answer. You can submit now.";
export function disabilityQuestion(who: string): string {
  return `Does ${who} have a disability as Washington law defines it? (Please don't name any condition.)`;
}
export function needQuestion(who: string, needObject: string): string {
  return `Does ${who} need ${needObject} because of their disability?`;
}
/** "with the change shown above it" (E2). DRAFT wording of the shown line. */
export function changeShownAbove(text: string): string {
  return `What they asked for: "${text}"`;
}
export const ANSWER_WEB = { yes: 'Yes', no: 'No', cantSay: "I can't say" } as const;
export const ANSWER_STAFF = { yes: 'Yes', no: 'No', cantSay: "They couldn't say" } as const;

// --- E3 verify page ---------------------------------------------------------
export function verifyRequestLine(subjectName: string, summary: string): string {
  return `${subjectName} has asked us to ${summary}.`;
}
export const VIEW_COPY_LINK = 'View your copy of their signed request';
export function verifyGenuineLine(officePhone: string): string {
  return `Questions, or want to check this is genuine? Call ${officePhone}.`;
}
export const RESPONDENT_NAME_LABEL = 'Your name (so we know who answered)';
export const VERIFY_THANKS =
  "Your answer has reached our team, and the reminders have stopped. There's nothing more you need to do.";

// --- F3 call sheet ------------------------------------------------------------
export const CALL_SHEET = {
  eyebrow: 'Front line call · Welcome Home Properties',
  heading: (verifierName: string): string => `Call ${verifierName}`,
  subline: (role: string, phone: string, subjectName: string): string =>
    [role, phone, `for ${subjectName}'s accommodation request`].filter((x) => x.trim() !== '').join(' · '),
  openHeading: 'How to open the call',
  frontDesk: (p: { callerName: string; verifierName: string; subjectName: string; questionCount: 1 | 2 }): string =>
    `To whoever answers: 'Hi, this is ${p.callerName} with Welcome Home Properties, a property management company in Walla Walla. I'm calling for ${p.verifierName}. ${p.subjectName} gave us signed, written permission to contact them with ${p.questionCount === 1 ? 'one quick yes-or-no question' : 'two quick yes-or-no questions'}. Is ${p.verifierName} available?' If asked what it's about: 'A housing request ${p.subjectName} made with us. We won't ask for any medical information.'`,
  onceYouHave: (p: { verifierName: string; subjectName: string; signedDate: string }): string =>
    `Once you have ${p.verifierName}: '${p.subjectName} signed a request with us on ${p.signedDate} and named you as someone who can confirm it. I won't ask about any diagnosis or treatment. If you'd rather have it in writing first, I can email you their signed request and permission right now.'`,
  voicemail: (p: { callerName: string; verifierName: string; subjectName: string; officePhone: string }): string =>
    `Voicemail, or anyone at a home number: 'This is ${p.callerName} from Welcome Home Properties in Walla Walla, calling for ${p.verifierName} on behalf of ${p.subjectName}. Please call us back at ${p.officePhone} and ask for ${p.callerName}. Thank you.' Never mention a disability, an accommodation, or an animal to anyone but ${p.verifierName}.`,
  dontRecord: "Don't record the call.",
  confirmHeading: 'What to confirm',
  confirmLead: (subjectName: string, summary: string): string =>
    `${subjectName} has asked us to ${summary}. Read the definition first, then ask only these. A yes, a no, or 'I can't say' from their own knowledge is all we need.`,
  permissionCovers: (firstSentence: string, signedDate: string): string =>
    `If they ask what the permission covers, read: '${firstSentence}' (signed ${signedDate}).`,
  noMedical: 'Do not ask for, or write down, a diagnosis, a condition name, or any medical detail.',
  requestHeading: 'The signed request',
  showRequest: 'Show the signed request',
  openInTab: 'Open in a new tab',
  requestUnavailable: "The signed request isn't available right now — the questions above are all you need.",
  earlierCalls: 'Earlier calls',
  notCaller: 'This call sheet is for the front-line callers. Ask an administrator to add you.', // DRAFT
} as const;

// --- G1 outcomes + G4 note ------------------------------------------------------
export const CALL_OUTCOME = {
  heading: 'What happened',
  not_reached: "I didn't reach them (no answer, or I left a message)",
  call_back: 'They asked us to call back',
  callBackWhen: 'When?',
  wants_writing: 'They want it in writing first',
  writingEmail: 'Their email',
  writingReadBack: (verifierName: string): string => `I read it back to them, and it's ${verifierName}'s or their office's address`,
  spoke: 'I spoke with them — answers below',
  cant_help_yet: "They can't help yet",
  why: 'Why?',
  betterPhone: 'Better phone number',
  allCantSaySteer:
    "If they couldn't say on every question, choose \"They can't help yet\" and \"They can't answer from what they know, or they declined\" instead.", // DRAFT
} as const;
export function cantHelpReasonLabel(
  reason: 'wrong_number' | 'wants_requester_first' | 'needs_own_release' | 'cannot_answer_or_declined' | 'says_already_answered' | 'other',
  names: { requesterName: string; signerName: string },
): string {
  switch (reason) {
    case 'wrong_number': return "Wrong number, or they're no longer there";
    case 'wants_requester_first': return `They want to hear from ${names.requesterName} first`;
    case 'needs_own_release': return `Their office needs ${names.signerName} to sign its own release form`;
    case 'cannot_answer_or_declined': return "They can't answer from what they know, or they declined";
    case 'says_already_answered': return 'They say they already answered';
    case 'other': return 'Something else';
  }
}
/** Short labels for "Earlier calls" and the console timeline — DRAFT. */
export const CALL_OUTCOME_SHORT = {
  not_reached: "Didn't reach them",
  call_back: 'Asked us to call back',
  wants_writing: 'Wants it in writing',
  spoke: 'Spoke — answered',
  cant_help_yet: "Can't help yet",
} as const;
export const CALL_NOTE = {
  label: 'Call logistics (optional)',
  labelRequired: 'Call logistics', // DRAFT — the label when "Something else" makes it required
  helper: (n: number): string =>
    `Only things like a better time to call or who to ask for. Never a diagnosis, condition, symptom, or treatment — even if they tell you. ${n}/300`,
} as const;

// --- A1 gate (staff; the SPA builds it, kept here so wording is one place) ----------
export const GATE = {
  heading: 'What do we already know?',
  disabilityQuestion: 'Is the disability apparent or already known to us?',
  needQuestion: 'Is the need for this change apparent or already known to us?',
  answers: { yes: 'Yes', no: 'No', not_known_yet: 'Not known yet' },
  rationaleLabel: 'Rationale (one line)',
  sourceLabel: 'Source of this knowledge',
  sources: [
    { value: 'requester_statement', label: 'Their signed statement' },
    { value: 'letter', label: 'A letter or document they gave us' },
    { value: 'observable', label: 'Readily observable' },
    { value: 'prior_tenancy', label: 'Prior tenancy / our records' },
  ],
  trainedServiceAnimal: 'Credibly a trained service animal (rare — skips verification)',
  inlineRule: "If they gave us a letter from someone who knows them, or both answers are yes, don't contact anyone. Decide the request.",
  revise: 'Revise',
  sendWithoutGate:
    "If you already know their disability and need, record what we know first. The form will then ask only what's still needed.",
} as const;

// --- A2 / A7 / A8 / H2 / G staff lines ------------------------------------------------
export const ATTENTION = {
  formSubmitted: (requesterName: string, confirmOnly: boolean): string =>
    confirmOnly ? `${requesterName} confirmed their request.` : `${requesterName} sent their request form.`, // DRAFT
  permissionAnswered: (signerName: string, answer: 'named' | 'letter'): string =>
    answer === 'named' ? `${signerName} named someone who can confirm the request.` : `${signerName} sent a letter.`, // DRAFT
  verifierAnswered: (verifierName: string, channel: 'form' | 'oral' | 'reply_email' | 'letter' | 'uploaded_document'): string =>
    `${verifierName} answered ${({ form: 'on the online form', oral: 'by phone', reply_email: 'by reply email', letter: 'by letter', uploaded_document: 'with a document' } as const)[channel]}.`, // DRAFT
  verifierNo: (verifierName: string, requesterName: string): string =>
    `${verifierName} answered No. A No is never a denial on its own — offer ${requesterName} another source.`, // DRAFT
  verifierNoEvidence: (verifierName: string, requesterName: string): string =>
    `${verifierName} couldn't confirm from what they know. Offer ${requesterName} another source.`, // DRAFT
  frontlineNeedsYou: (reasonLabel: string): string => `Front line needs you: ${reasonLabel}`,
  autoAttached: (name: string, address: string): string =>
    `Attached ${name}'s request to the application at ${address}. Check it's the right household.`,
  nameMismatch: (applicantName: string): string => `The name on the application is ${applicantName}.`, // DRAFT
  permissionWithdrawn: (staffName: string, signerName: string): string =>
    `${staffName} recorded that ${signerName} withdrew permission to contact.`, // DRAFT
  withdrawalNoticeUnsent: (names: string): string =>
    `Tell ${names} in writing that permission was withdrawn. We have no email for them.`, // DRAFT
  withdrawalNoticeFailed: (names: string): string =>
    `Tell ${names} in writing that permission was withdrawn. The email to them didn't go through.`, // DRAFT
  findAndRecord: (verifierName: string): string => `Find and record ${verifierName}'s answer.`, // DRAFT adaptation of "Find and record their answer"
  noPhone: (verifierName: string, requesterName: string): string => `No phone number for ${verifierName} — ask ${requesterName}`,
  confirmWritingAddress: (address: string): string => `Send to ${address}?`,
  confirmPhone: (verifierName: string, phone: string): string =>
    `Front line was given a new number for ${verifierName}: ${phone}. Confirm it before the next call.`, // DRAFT
  callsCap: (verifierName: string): string => `${verifierName}: 3 calls without reaching them`,
  possibleApplication: (name: string, address: string): string => `Possible application for ${name} at ${address} — attach?`,
  staleUnbound: (name: string): string => `Check in with ${name}: not yet applied`,
} as const;
export function waitingOnLine(p: { who: 'requester' | 'verifier' | 'front_line' | 'us'; since: string; businessDaysSinceHeard: number }): string {
  const who = { requester: 'the requester', verifier: 'a verifier', front_line: 'front line', us: 'us' }[p.who];
  const n = p.businessDaysSinceHeard;
  return `Waiting on ${who} since ${p.since} · ${n} business day${n === 1 ? '' : 's'} since we heard.`;
}
/** Decision 2 (Miles): A8 as an informational line. */
export function requestOpenLine(n: number): string {
  return `Request open for ${n} business days`;
}
